import './LoginScreen.scss';
import { FormController } from '@form-ts/core';
import { useForm, useFormWatch } from '@form-ts/react';
import { TranslationKey } from '@tolgee/react';
import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useAction } from 'src/hooks/useAction';
import { Button } from 'src/modules/common/components/Button';
import { Dropdown } from 'src/modules/common/components/Dropdown';
import { Modal } from 'src/modules/common/components/Modal';
import { TERMS_AND_PRIVACY_LINK_MAP } from 'src/modules/common/constants/links';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { GlobeIcon } from 'src/modules/common/icons/GlobeIcon';
import { BodySmallText } from 'src/modules/common/typography/BodySmallText';
import { getEnvironmentLanguages } from 'src/modules/config/selectors/getEnvironmentLanguages';
import { getLanguage } from 'src/modules/config/selectors/getLanguage';
import { CheckboxField } from 'src/modules/form/components/CheckboxField';
import { PasswordField } from 'src/modules/form/components/PasswordField';
import { TextField } from 'src/modules/form/components/TextField';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { LOGIN_PERFORM } from 'src/modules/login/actions/LoginActions';
import { LOGIN_FORM_SCHEMA } from 'src/modules/login/constants/schema';
import { LoginFormData } from 'src/modules/login/types/LoginFormData';
import { RequestAccessFormData } from 'src/modules/login/types/RequestAccessFormData';
import { RequestAccessModal } from 'src/modules/login/views/RequestAccessModal';
import { Language } from 'src/types/common/Language';

type Props = {
  readonly switchLanguage: (language: Language) => void;
  readonly submitRequestAccess: (form: FormController<RequestAccessFormData, FormError>) => void;
};

export const LoginScreen = ({ switchLanguage, submitRequestAccess }: Props): React.ReactElement => {
  const [isRequestAccessModalOpen, setRequestAccessModalOpen] = useState(false);
  const closeRequestForm = useCallback(() => setRequestAccessModalOpen(false), []);

  const intl = useIntl();

  const form = useForm<LoginFormData, FormError>('login', {
    reinitialize: false,
    initialValues: {
      username: '',
      password: '',
      remember: true,
    },
  });

  useFormValidator(form, LOGIN_FORM_SCHEMA);

  const formErrors = useFormWatch(form, form.errors.get);
  const isSubmitting = useFormWatch(form, form.submitting.get);
  const isSubmissionFailed = (
    !isSubmitting &&
    Object.keys(formErrors).length > 0 &&
    Object.values(formErrors).every((error) => error?.code === 'invalid')
  );

  const submitForm = useAction(LOGIN_PERFORM.request);
  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    submitForm(form);
  }, [form, submitForm]);

  const language = useSelector(getLanguage);
  const languages = useSelector(getEnvironmentLanguages);
  const termsAndPrivacyLink = TERMS_AND_PRIVACY_LINK_MAP[language];

  return (
    <>
      <div className="bp-login-screen">
        <div className="bp-login-screen__language-wrapper">
          <Dropdown
            options={languages}
            renderOption={(option) => <FormattedMessage id={LANGUAGE_KEYS[option]}/>}
            selectedOption={language}
            onChange={switchLanguage}
            theme="gray"
            icon={<GlobeIcon/>}
          />
        </div>

        <div className="bp-login-screen__form-wrapper">
          <div className="bp-login-screen__form-header">
            <h4 className="bp-login-screen__title">
              <FormattedMessage id="login/title"/>
            </h4>

            <div className="bp-login-screen__description-wrapper">
              <BodySmallText className="bp-login-screen__greeting">
                <FormattedMessage id="login/description"/>
              </BodySmallText>
            </div>
          </div>

          <div className="bp-login-screen__form-content">
            <form
              className="bp-login-screen__form"
              noValidate={true}
              autoComplete="off"
              onSubmit={handleSubmit}
            >
              <div className="bp-login-screen__form-inputs">
                <TextField
                  field={form.field.at('username')}
                  renderError={(error): React.ReactNode => (
                    error && error.code !== 'invalid'
                      ? <FormattedMessage id={`form/error/${error.code}`} values={error.context}/>
                      : ' '
                  )}
                  label={intl.formatMessage({ id: 'login/username' })}
                  type="email"
                  size="lg"
                  disabled={isSubmitting}
                  autoFocus={true}
                  autoComplete="username"
                  placeholder={intl.formatMessage({ id: 'login/emailPlaceholder' })}
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  description={null}
                />

                <PasswordField
                  field={form.field.at('password')}
                  renderError={(error): React.ReactNode => (
                    error && error.code !== 'invalid'
                      ? <FormattedMessage id={`form/error/${error.code}`} values={error.context}/>
                      : ' '
                  )}
                  label={intl.formatMessage({ id: 'login/password' })}
                  size="lg"
                  disabled={isSubmitting}
                  autoComplete="current-password"
                  placeholder={intl.formatMessage({ id: 'login/passwordPlaceholder' })}
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                />
              </div>

              <p className="bp-login-screen__form-error">
                {isSubmissionFailed ? (<FormattedMessage id="login/invalidCredentials"/>) : null}
              </p>

              <div className="bp-login-screen__form-extra">
                <div className="bp-login-screen__form-remember">
                  <CheckboxField
                    label={<FormattedMessage id="login/rememberMe"/>}
                    field={form.field.at('remember')}
                  />
                </div>
                <div className="bp-login-screen__form-forget">
                  <RouterLink to="/password/forgot" className="bp-login-screen__form-help-link">
                    <FormattedMessage id="login/forgotPassword"/>
                  </RouterLink>
                </div>
              </div>

              <Button
                className="bp-login-screen__button-submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                type="submit"
                icon={(
                  <div className="bp-login-screen__button-icon">
                    <ArrowRightIcon/>
                  </div>
                )}
              >
                <FormattedMessage id="login/submit"/>

              </Button>

              <p className="bp-login-screen__terms-and-privacy">
                <FormattedMessage
                  id="login/termsAndPrivacy"
                  values={{ link: link(termsAndPrivacyLink) }}
                />
              </p>

              <p className="bp-login-screen__form-help">
                <FormattedMessage id="login/needHelpMsg" values={{ mailTo }}/>
              </p>
            </form>
          </div>
        </div>
      </div>

      <Modal
        opened={isRequestAccessModalOpen}
        handleClose={closeRequestForm}
        size="md"
      >
        <RequestAccessModal
          onClose={closeRequestForm}
          onSubmit={submitRequestAccess}
        />
      </Modal>
    </>
  );
};

const mailTo = (children: React.ReactNode): React.ReactElement => (
  <a
    className="bp-login-screen__form-help-link"
    href="mailto:retrofit.feedback@belimo.de"
    rel="noopener noreferrer"
  >
    {children}
  </a>
);

const link = (termsAndPrivacyLink: string) => (children: React.ReactNode): React.ReactNode => (
  <a
    className="bp-login-screen__form-help-link"
    href={termsAndPrivacyLink}
    target="_blank"
    rel="noreferrer"
  >{children}
  </a>
);

const LANGUAGE_KEYS: Record<Language, TranslationKey> = {
  'en-US': 'login/language/en-US',
  'de-CH': 'login/language/de-CH',
  'fr-FR': 'login/language/fr-FR',
  'hu-HU': 'login/language/hu-HU',
  'es-ES': 'login/language/es-ES',
  'sv-SE': 'login/language/sv-SE',
  'zh-CN': 'login/language/zh-CN',
  'it-IT': 'login/language/it-IT',
  'sr-RS': 'login/language/sr-RS',
  'mk-MK': 'login/language/mk-MK',
  'ko-KR': 'login/language/ko-KR',
  'fi-FI': 'login/language/fi-FI',
};
